import { createApi } from '@reduxjs/toolkit/query/react';
import { groupBy } from 'lodash';
import {
  BalanceTransaction,
  ServiceCode,
  WithDateFilter,
  WithPaginationParams,
  type CreditOptions
} from 'types';
import { baseQuery } from './utils';

interface BalanceResponse {
  Balance: number;
  UpdatedAt: string;
  UpdateKind: number;
}

export enum PayType {
  Card = 0,
  Sbp = 1
}

export interface PurchaseInfo {
  PaymentType: number;
  Price: number;
  Currency: string;
  PurchaseDay: number;
}

export interface Service {
  Id: string;
  Name: string;
  Date: string;
  Assignment: number;
  PurchaseInfo: PurchaseInfo;
  Groups: string[];
  Code: ServiceCode;
  Desc: string;
}

export interface SubscriptionInfo {
  AccountId: string;
  ServiceId: string;
  Status: number;
  IsActive: boolean;
  PurchaseInfo: PurchaseInfo;
  DeviceId: string;
  Id: string;
  Lock: boolean;
}

export interface SubscribeData {
  userId: string;
  ServiceId: string;
  DeviceId: string;
}

export interface UnsubscribeData {
  userId: string;
  subscriptionId: string;
}

export interface BalanceOperation {
  userId: string;
  amount: string | number;
  description: string;
  international?: boolean;
  payType: PayType;
}

export type HistoryParams = WithPaginationParams<{
  userId: string;
}> &
  WithDateFilter;

interface HistoryResponse {
  Count: number;
  Items: BalanceTransaction[];
  Next?: string;
}

export const accountsService = createApi({
  baseQuery: baseQuery({ baseUrl: '/billing' }),
  tagTypes: ['Subs', 'Balance', 'Transactions'],
  reducerPath: 'accounts',
  endpoints: (build) => ({
    balance: build.query<BalanceResponse, string>({
      query: (userId: string) => ({
        url: `/accounts/${userId}/balance`,
        method: 'GET'
      }),
      keepUnusedDataFor: 0,
      providesTags: ['Balance']
    }),
    history: build.query<HistoryResponse, HistoryParams>({
      query: ({ userId, ...params }: HistoryParams) => ({
        url: `/accounts/${userId}/transactions`,
        params,
        method: 'GET'
      }),
      keepUnusedDataFor: 0,
      providesTags: ['Transactions']
    }),
    services: build.query<Service[], string>({
      query: (userId) => ({
        url: `/accounts/${userId}/services`,
        method: 'GET'
      })
    }),
    subscriptions: build.query<Record<string, SubscriptionInfo[]>, string>({
      query: (userId) => ({
        url: `/accounts/${userId}/subscriptions`,
        method: 'GET'
      }),
      transformResponse: (response: SubscriptionInfo[]) =>
        groupBy(response, (r) => r.DeviceId),
      providesTags: ['Subs']
    }),
    subscribe: build.mutation<unknown, SubscribeData>({
      query: ({ userId, ...data }) => ({
        url: `/accounts/${userId}/subscriptions`,
        method: 'POST',
        data
      }),
      invalidatesTags: ['Subs']
    }),
    unsubscribe: build.mutation<unknown, UnsubscribeData>({
      query: ({ userId, subscriptionId }) => ({
        url: `/accounts/${userId}/subscriptions/${subscriptionId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Subs']
    }),
    credit: build.mutation<
      { payUrl: string },
      Omit<BalanceOperation, 'description'>
    >({
      query: ({ userId, amount, international, payType }) => ({
        url: `/accounts/${userId}/balance/credit`,
        method: 'post',
        data: {
          Amount: +amount,
          Timestamp: new Date(),
          IsInternational: international,
          PayType: payType
        }
      }),
      invalidatesTags: ['Balance', 'Transactions']
    }),
    withdraw: build.mutation<unknown, BalanceOperation>({
      query: ({ userId, amount, description: Description }) => ({
        url: `/admin/accounts/${userId}/balance`,
        method: 'post',
        data: {
          Balance: +amount,
          Description
        }
      }),
      invalidatesTags: ['Balance', 'Transactions']
    }),
    creditOptions: build.query<CreditOptions, Partial<BalanceOperation>>({
      query: ({ userId, amount, international, payType }) => ({
        url: `/accounts/${userId}/balance/credit/options`,
        method: 'post',
        data: {
          Amount: amount === undefined ? amount : +amount,
          IsInternational: international,
          PayType: payType
        }
      })
    })
  })
});
